/**
 * "Personalize navigation" — IN PLACE, in the bar itself (ruling 50100).
 *
 * This file replaces `PersonalizeNav.tsx`, which opened a `Modal` sheet holding
 * a `NavigationEditor`. Comment 50100 opens *"The current separate Personalize
 * navigation screen is the wrong interaction model"* and row 1 requires the
 * entry points to put *"the existing, visible navbar into an editable mode in
 * place … Do not navigate to a second configuration screen"*. Mailbox #1706
 * restates it as standing: *"no standalone personalization screen where in-place
 * edit mode is required"*. So the sheet is deleted rather than wrapped, and
 * `AppShell` renders THIS inside the same `<aside>` the bar was already in.
 *
 * The account menu lands here. Command K remains the work surface (Go/Do/Ask),
 * so customization no longer competes with day-to-day actions inside it.
 *
 * ── THE LAYERS, and the contradiction this file refuses to resolve ─────────
 * Row 5 requires the UI to *"always say which layer is being edited"*. Four
 * comments describe four different ladders and no two agree:
 *
 *   46866        shipped default -> solution/tenant default -> persona default -> user
 *   46944 / ADR  platform recipe -> persona default -> user override
 *   50164        persona preset  -> tenant/global override -> user override
 *
 * **OPEN QUESTION, and it is not resolved here.** What this file names is what
 * `core.navigation(p_persona, p_host)` actually implements, because a client
 * that invents a rung will eventually disagree with the database about which bar
 * somebody has, and only one of those is the bar they were given. The door
 * (`packages/core/migrations/2026080406`, §4) does exactly three things:
 *
 *   1. `core.user_setting` where key = 'nav' and user_id = auth.uid()  -> the PIN
 *   2. ONE `core.experience` row, kind 'nav', ordered by specificity
 *      (`persona is not null` + `host is not null`), version desc
 *   3. NULL -> the client draws its own bar
 *
 * There is NO separate tenant rung: a deployment-wide default and a persona
 * default are the same table and the same resolution step, and the resolver
 * picks the more specific one. So this surface offers exactly two editable
 * layers, named for the two doors that write them:
 *
 *   "only you"      -> `core.pin_navigation(document | null)`, no authority
 *   "everyone here" -> `core.set_navigation(null, document, 'console')`, admin
 *
 * A PER-PERSONA default is still edited where it always was — the Navigation tab
 * on `/personas`, which has the persona picker and the host picker. That page is
 * an administrator configuring somebody else's experience, not a person
 * personalising their own bar, so 50100 does not reach it.
 *
 * ── the rung below a pin is a thing the door will not tell us ──────────────
 * `core.navigation()` returns the pin FIRST and carries no provenance, so once
 * you have pinned a bar there is no read that answers "what would I see without
 * it". `layerBelow` therefore gets the deployment default only when the caller
 * can read `core.navigation_defaults()` — i.e. when they are an admin. For
 * everybody else, a pinned bar reconciles against the built-in bar, which is
 * the pre-existing O3 curation leak, not a new one. Reported with the lane; the
 * fix is a door (`core.my_inherited_navigation()`), not a client.
 */
import {
  NavigationEditSurface, useToast,
  layerBelow, isNavDocument, effectiveNavigationRow,
  type NavDocument, type NavLayer,
} from '@solum/ui'
import { Link } from '@tanstack/react-router'
import { NavBar, useBuiltinNav } from './Nav'
import { useNavigation, useNavigationPin, usePinNavigation } from '../data/queries'
import { useNavigationDefaults } from '../experience/personas'
import s from './Nav.module.css'

/** Which of the door's two writable layers this edit lands on. */
/* One layer, and its words. The `default` rung is gone from this surface
   (ruling 54995 finding 1) — "Customize my navigation" may write only the
   user's own, and an admin sets the deployment bar on /personas → Navigation,
   where the persona and host are chosen explicitly. */
const LAYER_LABEL = { user: 'only you' } as const
const LAYER_NOTE = { user: 'Only you see this. Reset it any time.' } as const

const CONSOLE_HOST = 'console'

export function NavEditMode({ onClose }: { onClose: () => void }) {
  const builtin = useBuiltinNav()
  const nav = useNavigation()
  const pin = useNavigationPin()
  const setPin = usePinNavigation()
  /* The ADMIN door, called as a probe rather than as a claim. `core.
     navigation_defaults()` raises 42501 for a non-admin, so a successful answer
     IS the authority — there is no client-side role check here and no hidden
     control that the server would have allowed: a non-admin never sees the
     layer switch because the door refused to show them the layer. */
  const defaults = useNavigationDefaults()
  const toast = useToast()

  /* Kept as an authority PROBE, not a layer switch: `navigation_defaults()`
     raises 42501 for a non-admin, so a successful answer is the authority, and
     only somebody who has it is pointed at the admin surface. */
  const mayEditDefault = defaults.isSuccess

  /* The row `core.navigation` would resolve for a console caller with no
     persona — asked through the SHARED resolver rather than restated here
     (#1680). This file's own two-rung `??` was the closest of the three console
     copies to the door and still not it: no version tiebreak, and `2026081012`
     added that tiebreak precisely because two rows at one address resolved by
     physical row order until a VACUUM moved one. */
  const rows = defaults.data ?? []
  const defaultRow = effectiveNavigationRow(rows, null, CONSOLE_HOST)
  const defaultDoc = (defaultRow?.document ?? null) as NavDocument | null

  /* What the resolver answered, when that answer is NOT the caller's own pin.
     `core.navigation()` returns the pin first, so a pinned caller learns nothing
     about the rung below from this read (see the header). */
  const inherited = isNavDocument(pin.data) ? null : (isNavDocument(nav.data) ? nav.data : null)

  const base0 = builtin ?? { groups: [] }
  const layers: NavLayer[] = [
    { key: 'builtin', label: 'the built-in bar', document: base0 },
    /* The deployment rung is still SHOWN — a reader reconciling their own bar
       needs to see what sits under it — but it is not editable here. */
    { key: 'default', label: 'everyone here', document: defaultDoc ?? inherited },
    {
      key: 'user', label: LAYER_LABEL.user,
      document: isNavDocument(pin.data) ? pin.data : null, editing: true,
    },
  ]

  const stored = isNavDocument(pin.data) ? pin.data : null
  const editBase = layerBelow(layers) ?? base0
  const busy = setPin.isPending
  const ready = builtin !== null && nav.isSuccess && pin.isSuccess

  const save = (doc: NavDocument) => {
    setPin.mutate(doc, {
        onSuccess: () => { toast.success('Navigation saved', LAYER_NOTE.user); onClose() },
        onError: (e) => toast.error('Couldn’t save your navigation', (e as Error).message),
      })
  }

  const reset = () => {
    setPin.mutate(null, {
        onSuccess: () => { toast.success('Navigation reset', 'You are back on the bar you were given.'); onClose() },
        onError: (e) => toast.error('Couldn’t reset your navigation', (e as Error).message),
      })
  }

  return (
    <NavigationEditSurface
      base={editBase}
      value={stored}
      busy={busy}
      ready={ready}
      error={setPin.error}
      scope={LAYER_LABEL.user}
      scrollClassName={s.editScroll}
      countLabel={(count) => count === 0
        ? 'Nothing is showing — the bar will be empty.'
        : `${count} links showing`}
      excludedLabel={(count) =>
        `${count} unavailable ${count === 1 ? 'link was' : 'links were'} left out`}
      renderNavigation={(controller) => (
        <NavBar doc={editBase} testId="nav-editing" label="Platform"
                edit={{ ctl: controller, layerLabel: LAYER_LABEL.user }} />
      )}
      note={<>
        {LAYER_NOTE.user}
        {mayEditDefault && <span data-testid="nav-edit-default-elsewhere">
          {' '}Setting the bar for everyone? Use{' '}
          <Link to="/personas">Personas → Navigation</Link>.
        </span>}
      </>}
      onSave={save}
      onReset={stored ? reset : undefined}
      onClose={onClose}
    />
  )
}
